import {v4 as uuid} from "uuid";
import {computed, isReactive, isRef, nextTick, onMounted, ref, unref, watch} from "vue";
import {unwrap} from "@/utils/data";
import useDependencies from "@/compositions/utils/useDependencies";
import {isEqual, isFunction} from "lodash";
import {useComputedAsRef} from "@/compositions/useComputedAsRef";

const useQueryChanged = query => {
    const oldQuery = ref({
        ...query,
    });
    const hasChanged = ref(false);
    watch(query, value => {
        if (!isEqual(unref(value), unref(oldQuery.value))) {
            hasChanged.value = true;
            oldQuery.value = {
                ...value,
            };
            nextTick(() => {
                hasChanged.value = false;
            });
        }
    });
    return {
        hasChanged,
    };
};

export const useModels = ({
    model,
    query = {},
    filter = null,
    limits = {page: 1, limit: 999},
    relationships = [],
    enabled = true,
    dependencies = {},
    events: {onRemoved = null, beforeLoad} = {},
}) => {
    const share = Object.keys(query).length === 0;

    const queryKeys = Object.keys(query).join(",");

    const queryKey = [model.entity, "all", share ? "shared" : uuid(), !share ? queryKeys : undefined];

    const fetchF = async () => await model.FetchAll(limits, unwrap(query), relationships);

    const loading = ref(false);

    const refetch = async () => {
        loading.value = true;
        beforeLoad && (await beforeLoad());
        await fetchF();
        loading.value = false;
    };

    const {shouldRun} = useDependencies(dependencies);

    const filterF = computed(() => {
        const filters = filter ? unwrap(filter) : unwrap(query);
        return value => {
            return Object.keys(filters).every(key => {
                const filterValue = unref(filters[key]);
                if (filterValue === undefined) {
                    return true;
                }
                if (Object.keys(value).includes(key) === false) {
                    return true;
                }
                const ormValue = unref(value[key]);
                if (Array.isArray(filterValue)) {
                    return filterValue.includes(ormValue);
                }
                if (filterValue && typeof filterValue === "object") {
                    if (key === "$or") {
                        return Object.keys(filterValue).some(key => {
                            const orValue = unref(filterValue[key]);
                            const ormValue = unref(value[key]);
                            if (Array.isArray(orValue)) {
                                return orValue.includes(ormValue);
                            }
                            return orValue === ormValue;
                        });
                    }
                }
                return filterValue === ormValue;
            });
        };
    });

    const queryEnabled = computed(() => {
        return unref(shouldRun) && unref(enabled);
    });

    // TODO - Reimplement when VueQuery is back
    // const { isFetching } = useQuery({
    //   queryKey,
    //   queryFn: fetchF,
    //   refetchOnWindowFocus: false,
    //   refetchOnMount: false,
    //   enabled: queryEnabled,
    // });

    if (isRef(query) || isReactive(query) || isFunction(query)) {
        const {hasChanged} = useQueryChanged(query);
        watch(hasChanged, async value => {
            if (value && unref(queryEnabled)) {
                await refetch();
            }
        });
        watch(queryEnabled, async value => {
            if (value && !unref(hasChanged)) {
                await refetch();
            }
        });
    } else {
        watch(queryEnabled, async value => {
            if (value) {
                await refetch();
            }
        });
    }

    onMounted(async () => {
        if (queryEnabled.value) {
            await refetch();
        }
    });

    const data = computed(() => {
        return model.query().where(filterF.value).with(relationships).get();
    });

    const remove = async id => {
        const response = await model.Delete(id);
        onRemoved && onRemoved(response);
        return response;
    };

    // TODO - Reimplement when VueQuery is back
    // if (!share) {
    //   onUnmounted(() => {
    //     queryClient.removeQueries(queryKey);
    //   });
    // }

    const dataRef = useComputedAsRef(data)

    const debug = {
        queryKey,
        queryEnabled,
        share,
        shouldRun,
        dependencies,
    };

    return {
        dataRef,
        remove,
        data,
        loading,
        debug,
        refetch,
    };
};
